import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiGithub,
  FiExternalLink,
  FiLock,
  FiX,
  FiArrowUpRight,
} from "react-icons/fi";
import parse from "html-react-parser";
import { PortFolioData } from "../../utils/http";
import SectionHeading from "../../components/SectionHeading";
import SectionArt from "../../components/SectionArt";
import Reveal from "../../components/Reveal";
import TiltCard from "../../components/TiltCard";
import { useLenis } from "../../components/SmoothScroll";

const FILTERS = [
  { key: "all", label: "All Work" },
  { key: "live", label: "Live" },
  { key: "oss", label: "Open Source" },
];

const yearOf = (d) => (d ? d.split("/").pop() : null);
const rangeOf = (s, e) => `${yearOf(s)} — ${e ? yearOf(e) : "Present"}`;

const ProjectLinks = ({ item, onLinkClick }) => (
  <>
    {item.repoType !== "private" && item.github ? (
      <a
        href={item.github}
        target="_blank"
        rel="noreferrer"
        onClick={onLinkClick}
        className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-fill px-4 py-2 text-xs font-semibold text-content transition-colors hover:border-hairline hover:bg-fill-2"
      >
        <FiGithub /> Code
      </a>
    ) : (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-4 py-2 text-xs font-semibold text-faint">
        <FiLock /> Private
      </span>
    )}
    {item.livelink && (
      <a
        href={item.livelink}
        target="_blank"
        rel="noreferrer"
        onClick={onLinkClick}
        className="inline-flex items-center gap-1.5 rounded-full bg-content px-4 py-2 text-xs font-semibold text-surface transition-all hover:opacity-90"
      >
        <FiExternalLink /> Live
      </a>
    )}
  </>
);

const Portfolio = () => {
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const lenis = useLenis();

  const filtered = useMemo(() => {
    if (filter === "live") return PortFolioData.filter((p) => p.livelink);
    if (filter === "oss")
      return PortFolioData.filter((p) => p.repoType !== "private" && p.github);
    return PortFolioData;
  }, [filter]);

  const shown = showAll ? filtered : filtered.slice(0, 6);

  // Lock background scroll + close on Escape while the detail modal is open.
  useEffect(() => {
    if (!selected) return undefined;
    lenis?.stop();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      lenis?.start();
    };
  }, [selected, lenis]);

  const stop = (e) => e.stopPropagation();

  return (
    <section id="portfolio" className="relative px-6 py-28 md:py-36">
      <div className="container mx-auto max-w-6xl">
        <SectionArt type="work" className="mx-auto mb-4 h-32 w-48" />
        <SectionHeading
          eyebrow="Portfolio"
          title="Selected Work"
          subtitle="A selection of products and platforms I've designed and engineered — from SaaS tools to automation systems. Tap any card for the full story."
        />

        {/* filter tabs */}
        <Reveal direction="up">
          <div className="mb-12 flex flex-wrap justify-center gap-2">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => {
                    setFilter(f.key);
                    setShowAll(false);
                  }}
                  className="relative rounded-full px-5 py-2 text-sm font-medium transition-colors"
                >
                  {active && (
                    <motion.span
                      layoutId="filter-pill"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      className="absolute inset-0 rounded-full bg-content shadow-glow"
                    />
                  )}
                  <span
                    className={`relative z-10 ${
                      active ? "text-surface" : "text-muted hover:text-content"
                    }`}
                  >
                    {f.label}
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* grid */}
        <motion.div layout className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {shown.map((item, index) => (
              <motion.div
                key={item.projectname}
                layout
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{
                  duration: 0.5,
                  delay: (index % 6) * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <TiltCard
                  onClick={() => setSelected(item)}
                  className="group glass flex h-full cursor-pointer flex-col rounded-3xl p-6"
                >
                  <div className="mb-4 flex items-center justify-between">
                    {item.startDate ? (
                      <span className="rounded-full border border-hairline bg-fill px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted">
                        {rangeOf(item.startDate, item.endData)}
                      </span>
                    ) : (
                      <span />
                    )}
                    {!item.endData && (
                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                        Active
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-semibold text-content transition-colors group-hover:text-gradient">
                    {item.projectname}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {item.techstacks.slice(0, 5).map((stack, idx) => (
                      <span
                        key={idx}
                        className="rounded-md border border-hairline bg-fill px-2 py-0.5 text-[11px] text-content"
                      >
                        {stack}
                      </span>
                    ))}
                    {item.techstacks.length > 5 && (
                      <span className="rounded-md border border-hairline px-2 py-0.5 text-[11px] text-faint">
                        +{item.techstacks.length - 5}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 line-clamp-3 text-sm leading-relaxed text-muted">
                    {parse(item.description)}
                  </div>

                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-content transition-colors group-hover:text-content">
                    View details
                    <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>

                  <div className="mt-auto flex gap-3 pt-6">
                    <ProjectLinks item={item} onLinkClick={stop} />
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length > 6 && (
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="rounded-full border border-hairline bg-fill px-7 py-3 text-sm font-semibold text-content backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-fill-2 hover:shadow-glow"
            >
              {showAll ? "Show less" : `Show all ${filtered.length} projects`}
            </button>
          </div>
        )}
      </div>

      {/* detail modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
          >
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setSelected(null)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="glass-strong relative z-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl p-7 sm:p-9"
              role="dialog"
              aria-modal="true"
              aria-label={selected.projectname}
            >
              <button
                onClick={() => setSelected(null)}
                aria-label="Close"
                className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-hairline bg-fill text-muted transition-colors hover:bg-fill-2 hover:text-content"
              >
                <FiX />
              </button>

              <div className="mb-4 flex flex-wrap items-center gap-3 pr-10">
                {selected.startDate && (
                  <span className="rounded-full border border-hairline bg-fill px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted">
                    {rangeOf(selected.startDate, selected.endData)}
                  </span>
                )}
                {!selected.endData && (
                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                    Active
                  </span>
                )}
              </div>

              <h3 className="text-2xl font-bold text-content sm:text-3xl">
                {selected.projectname}
              </h3>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {selected.techstacks.map((stack, idx) => (
                  <span
                    key={idx}
                    className="rounded-md border border-hairline bg-fill px-2.5 py-1 text-xs text-content"
                  >
                    {stack}
                  </span>
                ))}
              </div>

              <div className="mt-6 space-y-3 text-[15px] leading-relaxed text-muted">
                {parse(selected.description)}
              </div>

              <div className="mt-8 flex gap-3">
                <ProjectLinks item={selected} onLinkClick={stop} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
